import Phaser from 'phaser';
import { speakRoundIntro } from '@/game/roundIntro';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK, MAZE } from '@/game/config';
import { getBackground, getBestMaze, setBestMaze, type Background } from '@/game/storage';
import { fableVariant } from '@/game/theme';
import { Starfield } from '@/game/objects/Starfield';
import { addRoundedLabelBg, pillTexture } from '@/game/ui/roundedLabelBg';
import { addMuteToggle } from '@/game/ui/muteToggle';
import { scriptFontFamily } from '@/game/fonts';
import { wordIconKey, wordIconUrl } from '@/game/wordIcons';
import { getPictureWordsPool } from '@/data/pools';
import type { PackClass } from '@/data/packs/types';
import { ReadingProgression } from '@/game/progression/ReadingProgression';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { maybeAskForRating } from '@/game/rating';
import { trackMazeStart, trackMazeEnd } from '@/analytics';
import {
  playPopSound,
  playCrackSound,
  playThudSound,
  playRecordSound,
  playRoundCompleteSound,
  speakRulePrompt,
  startBackdropAmbience,
  stopBackdropAmbience,
} from '@/game/audio';
import {
  buildBoard, eligibleWords, type MazeBoard, type MazeWord, type Placement,
} from '@/game/maze/grid';
import {
  lineBetween, tapPath, resolveSelection, flakesAfterBoard, type Path,
} from '@/game/maze/select';
import { redirectIfWebLimit } from '@/game/ui/webLimitCard';

interface MazeInit {
  lang: string;
  /** Dev only (`?maze=4`): start the ladder further up, where the diagonals live. */
  startRound?: number;
}

/**
 * The play area the grid is laid out in: below the banner and its clock, above the mute toggle.
 *
 * The ask is a banner across the top now, the way every lesson asks its question (BoardScene,
 * SurvivalScene), so there is no side card and the grid is free to use the full width. Height is
 * what caps the tile size, which is why the ladder grows sideways (config.MAZE.grids).
 */
const PLAY = { x: 90, y: 132, w: 1100, h: 528 };

/** The banner row, on SurvivalScene's line: the ask at y 56, its clock directly under it. */
const BANNER_Y = 56;
const CLOCK_Y = 100;
const CLOCK_W = 420;
const CLOCK_FILL = 0x7ec4ff;
/** The clock's last quarter, in the colour the rest of the game spends on urgency. */
const CLOCK_LOW = 0xff8f8f;

const FLAKE = '❄️';
const TILE_FILL = 0xe8f4fb;
const TILE_EDGE = 0x8fb6d8;
const TILE_TEXT = '#1a2430';
const SELECT_FILL = 0x7ec4ff;
const FOUND_FILL = 0xbfefff;
/** The frost that takes the letters nobody was looking for, at time up. */
const FROZEN_FILL = 0xffffff;
const FROZEN_TEXT = '#6fa8c8';
/**
 * The words themselves, revealed after the noise has frozen — frost with a violet cast.
 *
 * A different colour, not a brighter one: white ice is what the board turns into, and the answer
 * has to be legible *against* it rather than as more of it. Violet also stays out of the way of the
 * two colours this mode already spends — the blue of a word you took and the gold of the banner.
 */
const REVEAL_FILL = 0xd9c2f2;
const REVEAL_TEXT = '#3b2166';

interface Tile {
  col: number;
  row: number;
  plate: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  found: boolean;
  /** The crystal grown over this tile at time up, so the shatter can take it down too. */
  ice?: Phaser.GameObjects.Graphics;
}

/**
 * Ice Maze — the Neptune reward minigame (specs/word-maze-mode.md).
 *
 * A letter grid with words frozen into it, and one question: "Find 4 words: Fruit". There is no
 * list of what to look for, which is the whole design — a word search that prints its words can be
 * solved by matching shapes, without reading one of them. Here the child reads the grid, and the
 * hazards are planted decoy words from OTHER topics (maze/select.ts), so the task is the lesson's
 * own class-match rule in grid form.
 *
 * Self-contained like SurvivalScene and PairsScene: it touches neither RoundScene/BoardScene nor
 * the Leitner engine, and is entered only by spending a banked Ice Cube. What is on the board is
 * decided by the Phaser-free generator in game/maze/grid.ts; this scene only draws it.
 */
export class MazeScene extends Phaser.Scene {
  private lang!: string;
  private startRound = 1;

  private pool: MazeWord[] = [];
  private classes: PackClass[] = [];
  private usedTopics = new Set<string>();

  private round = 1;
  private flakes = MAZE.startFlakes;
  private flakesLostThisBoard = 0;
  private wordsFound = 0;
  private best = 0;

  private board: MazeBoard | null = null;
  private tiles: Tile[][] = [];
  /** Texture keys of the baked tile plates, one per (cell size, fill) — see tileTexture. */
  private tileTexKeys: string[] = [];
  private foundIds = new Set<string>();
  private cell = 60;
  private gridX = 0;
  private gridY = 0;

  /** Where a press started (drag in progress), and the run tapped out letter by letter. */
  private pressCell: [number, number] | null = null;
  private tapped: Path = [];
  private preview: Path = [];
  private selectLayer!: Phaser.GameObjects.Graphics;

  private msLeft = 0;
  private running = false;
  private over = false;

  private flakeIcons: Phaser.GameObjects.Text[] = [];
  private roundText!: Phaser.GameObjects.Text;
  private foundText!: Phaser.GameObjects.Text;
  private askText!: Phaser.GameObjects.Text;
  private askBg: Phaser.GameObjects.NineSlice | null = null;
  /** The clock's fill, stretched over a fixed track. A nine-slice rather than a Graphics — see
   *  paintClock. */
  private clockFill!: Phaser.GameObjects.NineSlice;
  /** What the clock bar was last drawn as: its fill width in whole game units, and whether it was
   *  in the warning colour. -1 means "nothing drawn yet" and forces the next paint. */
  private clockPx = -1;
  private clockLow = false;
  private catchIcons: Phaser.GameObjects.GameObject[] = [];
  /** The ice crystals grown over the tiles at time up — cleared with the board (§5.5). */
  private frost: Phaser.GameObjects.Graphics[] = [];

  constructor() {
    super({ key: 'Maze' });
  }

  init(data: MazeInit): void {
    this.lang = data.lang;
    this.startRound = Math.max(1, Math.floor(data.startRound ?? 1));
    this.round = this.startRound;
    this.flakes = MAZE.startFlakes;
    this.flakesLostThisBoard = 0;
    this.wordsFound = 0;
    this.tiles = [];
    this.board = null;
    this.foundIds = new Set();
    this.usedTopics = new Set();
    this.pressCell = null;
    this.tapped = [];
    this.preview = [];
    this.running = false;
    this.over = false;
  }

  preload(): void {
    // The player's chosen backdrop, loaded on demand exactly as Pairs does it — this mode has no
    // exclusive backdrop either.
    const bg = getBackground();
    if (bg !== 'space') {
      const asset = backgroundAsset(bg);
      if (!this.textures.exists(`bg_${asset}`)) {
        this.load.image(`bg_${asset}`, `/images/background_${asset}.jpg`);
      }
    }
  }

  create(): void {
    if (redirectIfWebLimit(this)) return;   // free web build used up — see webLimitCard.ts
    this.best = getBestMaze();
    trackMazeStart();   // web demo only

    this.drawBackdrop();
    startBackdropAmbience(getBackground());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      stopBackdropAmbience();
      this.clearTileTextures();
    });

    // A hand, and deliberately NOT `body.in-game` — nothing here is aimed at, everything is
    // tapped or dragged. Same reasoning as PairsScene.
    this.input.setDefaultCursor('pointer');

    this.buildPool();
    this.drawHud();
    this.selectLayer = this.add.graphics().setDepth(20);

    addMuteToggle(this, {
      depth: 50,
      onToggle: (muted) => {
        if (muted) stopBackdropAmbience();
        else startBackdropAmbience(getBackground());
      },
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.onDown(p));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => this.onMove(p));
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => this.onUp(p));

    this.showHowTo(() => this.startBoard(this.round));
  }

  update(_time: number, delta: number): void {
    if (!this.running || this.over) return;
    this.msLeft -= delta;
    this.paintClock();
    if (this.msLeft <= 0) {
      this.running = false;
      this.onTimeUp();
    }
  }

  // ── content ──────────────────────────────────────────────────────────────────

  /**
   * The words this run draws from: the PICTURE pool for the player's language, ordered
   * "already-learned first" by the same routine Survival and Pairs use.
   *
   * The picture pool whatever lesson is active — an Ice Cube earned in the Fables journey still
   * opens a picture-word maze. The mode needs data of a shape only that pool has (short concrete
   * nouns, topic-labelled, with icons), and a treat should be the same game for every child.
   */
  private buildPool(): void {
    const pool = getPictureWordsPool(this.lang) ?? getPictureWordsPool('en');
    if (!pool) return;
    // Read-only use: beginSession() is deliberately NOT called — a reward must not write to the
    // journey.
    const words = new ReadingProgression(this.lang, pool).survivalWords();
    this.pool = eligibleWords(words, this.lang).slice(0, MAZE.workingSetSize * 4);
    this.classes = pool.classes ?? [];
  }

  // ── boards ───────────────────────────────────────────────────────────────────

  private startBoard(round: number): void {
    this.round = round;
    this.flakesLostThisBoard = 0;
    this.foundIds = new Set();
    this.clearSelection();
    this.roundText.setText(i18next.t('maze.round', { n: round }));

    const board = buildBoard({
      round,
      pool: this.pool,
      classes: this.classes,
      usedTopics: this.usedTopics,
    });
    if (!board) { this.gameOver(); return; }
    this.usedTopics.add(board.topic);
    // Every topic asked once → start the rotation again rather than repeating one forever.
    if (this.usedTopics.size >= this.classes.length) this.usedTopics = new Set([board.topic]);
    this.board = board;

    this.setAsk(i18next.t('maze.find', { n: board.targets.length, topic: board.topicLabel }));
    // Read the ask aloud for the opening rounds (game/roundIntro.ts). Tapping the banner
    // re-speaks it at any point, which is the escape hatch for a child who missed it.
    speakRoundIntro(this.askText.text, this.lang, round);
    this.foundText.setText(`0 / ${board.targets.length}`);

    // Load only this board's icons — precached by the service worker, so a cache read rather than
    // a request (the same load-what-you-show policy BoardScene and Pairs use).
    const missing = board.targets.filter((p) => !this.textures.exists(wordIconKey(p.word.id)));
    if (missing.length === 0) { this.layoutBoard(board); return; }
    for (const p of missing) this.load.image(wordIconKey(p.word.id), wordIconUrl(p.word.id));
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.layoutBoard(board));
    this.load.start();
  }

  private layoutBoard(board: MazeBoard): void {
    for (const row of this.tiles) for (const t of row) { t.plate.destroy(); t.label.destroy(); }
    this.tiles = [];
    this.clearTileTextures();   // the cell size changes with the rung; last board's plates are dead
    for (const o of this.catchIcons) o.destroy();
    this.catchIcons = [];
    for (const f of this.frost) f.destroy();
    this.frost = [];
    this.selectLayer.clear();

    this.cell = Math.floor(Math.min(PLAY.w / board.cols, PLAY.h / board.rows));
    this.gridX = Math.round(PLAY.x + (PLAY.w - this.cell * board.cols) / 2);
    this.gridY = Math.round(PLAY.y + (PLAY.h - this.cell * board.rows) / 2);

    const fontSize = Math.round(this.cell * 0.48);
    for (let r = 0; r < board.rows; r++) {
      const row: Tile[] = [];
      for (let c = 0; c < board.cols; c++) {
        const { x, y } = this.cellCentre(c, r);
        const plate = this.add.image(x, y, this.tileTexture(TILE_FILL)).setDepth(10);
        // Plain letters, no outline: wordTextStyle's 6px stroke exists to hold a white word
        // legible over a photo backdrop, and on a light tile it just inks the letter until a grid
        // of them reads as a wall. The face still comes from the language's own script.
        const label = this.add.text(x, y, board.grid[r][c], {
          fontFamily: scriptFontFamily(this.lang), fontSize: `${fontSize}px`, color: TILE_TEXT,
        }).setOrigin(0.5).setDepth(30);
        row.push({ col: c, row: r, plate, label, found: false });
      }
      this.tiles.push(row);
    }

    // The board arrives rather than appearing — a frost-in from the top-left corner.
    this.tiles.flat().forEach((t) => {
      t.plate.setAlpha(0);
      t.label.setAlpha(0);
      this.tweens.add({
        targets: [t.plate, t.label], alpha: 1, duration: 200,
        delay: (t.col + t.row) * 26,
      });
    });

    this.msLeft = board.timeMs;
    this.clockPx = -1;   // new board, full bar: force the first paint (see paintClock)
    this.paintClock();
    this.time.delayedCall((board.cols + board.rows) * 26 + 260, () => {
      if (this.over) return;
      this.running = true;
      // The ask is spoken as well as written: the whole task is one sentence, and the child this
      // is built for cannot yet read it reliably.
      speakRulePrompt(this.askText.text, this.lang);
    });
  }

  private cellCentre(col: number, row: number): { x: number; y: number } {
    return {
      x: this.gridX + this.cell * (col + 0.5),
      y: this.gridY + this.cell * (row + 0.5),
    };
  }

  private cellAt(x: number, y: number): [number, number] | null {
    const board = this.board;
    if (!board) return null;
    const c = Math.floor((x - this.gridX) / this.cell);
    const r = Math.floor((y - this.gridY) / this.cell);
    if (c < 0 || r < 0 || c >= board.cols || r >= board.rows) return null;
    return [c, r];
  }

  /**
   * The tile plate, baked once per (size, colour) and drawn as an Image.
   *
   * Each plate used to be a Graphics holding a filled and a stroked rounded rect. Retained, not
   * redrawn — which sounds free and is not: Phaser 3 caches no Graphics geometry, so every frame it
   * re-walks each plate's command buffer, re-tessellates the two rects (four arcs apiece, ~100
   * points each — `iterStep` is 0.01 in GraphicsWebGLRenderer) and re-triangulates the fill. At the
   * top rung that is 104 plates ≈ 84,000 point allocations and 104 earcut passes EVERY FRAME.
   * Measured on a desktop dev build: 42-57 ms/frame with the plates on screen, 0.9-1.4 ms with them
   * hidden — the grid alone was the entire frame budget, and this mode is meant for a tablet.
   *
   * Baked at 1:1 because that is exactly what the vector path was rasterized into anyway: the
   * WebGL canvas is a 1280x720 drawing buffer that the browser scales (Scale.FIT), so the texture
   * is pixel-identical to what Graphics drew, at one shared texture and one batched draw call.
   */
  private tileTexture(fill: number): string {
    const s = this.cell - 6;
    const key = `maze-tile-${s}-${fill.toString(16)}`;
    if (this.textures.exists(key)) return key;
    // 2px of margin so the 2px stroke, which straddles the path, keeps both of its halves.
    const pad = 2;
    const g = this.make.graphics({ x: 0, y: 0 }, false);
    g.fillStyle(fill, 0.96).fillRoundedRect(pad, pad, s, s, 10);
    g.lineStyle(2, TILE_EDGE, 0.8).strokeRoundedRect(pad, pad, s, s, 10);
    g.generateTexture(key, s + pad * 2, s + pad * 2);
    g.destroy();
    this.tileTexKeys.push(key);
    return key;
  }

  /** Repaint a tile by swapping which baked plate it wears. Same size for every fill, so nothing
   *  else about the sprite changes. */
  private setTileFill(plate: Phaser.GameObjects.Image, fill: number): void {
    plate.setTexture(this.tileTexture(fill));
  }

  /** Drop the baked plates. The cell size changes with the rung, so last board's textures are dead
   *  weight — and nothing in Phaser evicts a texture on its own. */
  private clearTileTextures(): void {
    for (const key of this.tileTexKeys) this.textures.remove(key);
    this.tileTexKeys = [];
  }

  // ── selecting ────────────────────────────────────────────────────────────────

  private onDown(p: Phaser.Input.Pointer): void {
    if (!this.running || this.over) return;
    const cell = this.cellAt(p.worldX, p.worldY);
    if (!cell) return;
    // A press is the start of a possible drag. Whether it was a drag or a tap is only known on
    // release (onUp), so the tapped-out run underneath is left alone until then.
    this.pressCell = cell;
    this.preview = this.tapped.length > 0 ? this.tapped : [cell];
    this.paintSelection();
  }

  private onMove(p: Phaser.Input.Pointer): void {
    if (!this.running || this.over || !this.pressCell || !p.isDown) return;
    const cell = this.cellAt(p.worldX, p.worldY);
    if (!cell) return;
    const line = lineBetween(this.pressCell, cell);
    if (line) this.preview = line;
    this.paintSelection();
  }

  private onUp(p: Phaser.Input.Pointer): void {
    if (!this.running || this.over || !this.pressCell) return;
    const cell = this.cellAt(p.worldX, p.worldY);
    const from = this.pressCell;
    this.pressCell = null;
    if (!cell) { this.clearSelection(); return; }

    // Released somewhere else: that was a drag, and it stands on its own.
    if (cell[0] !== from[0] || cell[1] !== from[1]) {
      const line = lineBetween(from, cell);
      this.clearSelection();
      if (line && line.length > 1) this.resolve(line);
      return;
    }

    // Released where it started: a tap. Taps build the word up one letter at a time — B, A, N, A,
    // N, E — which is what a five-year-old does with a grid; the rules for what one tap does to
    // the run live in maze/select.ts. A tap that completes a word takes it; otherwise the run just
    // stands there waiting for the next letter, and costs nothing either way.
    this.tapped = tapPath(this.tapped, cell);
    this.preview = this.tapped;
    this.paintSelection();
    if (this.tapped.length < 2) return;
    if (resolveSelection(this.board!, this.tapped, this.foundIds).kind === 'target') {
      const run = this.tapped;
      this.clearSelection();
      this.resolve(run);
    }
  }

  private clearSelection(): void {
    this.pressCell = null;
    this.tapped = [];
    this.preview = [];
    this.paintSelection();
  }

  /** The capsule under the current drag — one connected shape, because it is a line being drawn. */
  private paintSelection(): void {
    this.selectLayer.clear();
    if (this.preview.length === 0) return;
    this.selectLayer.fillStyle(SELECT_FILL, 0.45);
    for (const [c, r] of this.preview) {
      const { x, y } = this.cellCentre(c, r);
      const s = this.cell - 2;
      this.selectLayer.fillRoundedRect(x - s / 2, y - s / 2, s, s, 12);
    }
  }

  private resolve(path: Path): void {
    const board = this.board;
    if (!board) return;
    const sel = resolveSelection(board, path, this.foundIds);

    if (sel.kind === 'target') { this.onFound(sel.placement); return; }
    // Nothing, or a word already frozen: a short shake and no cost. Nothing on the board can cost
    // a snowflake — only the clock can (specs/word-maze-mode.md §5.3).
    this.shake(path);
  }

  private onFound(p: Placement): void {
    this.foundIds.add(p.word.id);
    this.wordsFound += 1;
    playPopSound();
    // Hearing the word you just read is the mode's teaching moment — the same one Pairs gets from
    // turning a word card.
    speakRulePrompt(p.word.text, this.lang);

    for (const [c, r] of p.path) {
      const tile = this.tiles[r][c];
      tile.found = true;
      this.setTileFill(tile.plate, FOUND_FILL);
      tile.label.setColor('#0b3b52');
      this.tweens.add({
        targets: [tile.plate, tile.label], scale: 1.12, duration: 120, yoyo: true, ease: 'Quad.easeOut',
      });
    }

    this.addCatch(p);
    const board = this.board!;
    this.foundText.setText(`${this.foundIds.size} / ${board.targets.length}`);
    if (this.foundIds.size >= board.targets.length) {
      this.running = false;
      this.time.delayedCall(450, () => this.completeBoard());
    }
  }

  /** The found word's picture joins the stack beside the grid — a reward that reveals nothing. */
  private addCatch(p: Placement): void {
    const board = this.board!;
    const x = this.gridX + this.cell * board.cols + (GAME_WIDTH - (this.gridX + this.cell * board.cols)) / 2;
    const y = PLAY.y + 60 + this.catchIcons.length * 86;
    const key = wordIconKey(p.word.id);
    const from = this.cellCentre(p.path[0][0], p.path[0][1]);

    const icon = this.textures.exists(key)
      ? this.add.image(from.x, from.y, key).setDisplaySize(64, 64)
      : this.add.text(from.x, from.y, '🧊', { fontFamily: FONT_STACK, fontSize: '48px' }).setOrigin(0.5);
    icon.setDepth(40);
    this.catchIcons.push(icon);
    this.tweens.add({ targets: icon, x, y, duration: 420, ease: 'Cubic.easeInOut' });
  }

  private shake(path: Path): void {
    for (const [c, r] of path) {
      const tile = this.tiles[r]?.[c];
      if (!tile) continue;
      this.tweens.add({ targets: [tile.plate, tile.label], x: `+=4`, duration: 45, yoyo: true, repeat: 1 });
    }
  }

  private loseFlake(): void {
    this.flakes = Math.max(0, this.flakes - 1);
    this.flakesLostThisBoard += 1;
    this.paintFlakes();
    playThudSound();
    if (this.flakes === 0) {
      this.running = false;
      this.time.delayedCall(650, () => this.gameOver());
    }
  }

  private completeBoard(): void {
    const before = this.flakes;
    this.flakes = flakesAfterBoard(this.flakes, this.flakesLostThisBoard, MAZE.startFlakes);
    const gained = this.flakes > before;
    this.paintFlakes();
    playRoundCompleteSound();

    const lines = [i18next.t('maze.round', { n: this.round + 1 })];
    if (gained) lines.push(i18next.t('maze.flakeBack'));
    this.banner(lines.join('\n'), '#ffd166', () => this.startBoard(this.round + 1));
  }

  /**
   * The clock ran out: the grid freezes, shatters and puffs away, one flake melts, and the SAME
   * rung is dealt again with new words (§5.5). Rounds advance on a cleared board only — a board
   * you did not solve should not promote you.
   *
   * The freeze runs in **two beats, and the order is the point**. First the letters nobody was
   * looking for ice over, sweeping in from the left; a moment later, the words that were hidden in
   * there freeze too, in violet, so they are the last thing standing on a white board. A child who
   * ran out of time gets to see what they were hunting — and sees it *emerge* from the noise,
   * which is a small lesson in itself, rather than being told in a list they can no longer act on.
   */
  private onTimeUp(): void {
    this.clearSelection();
    const tiles = this.tiles.flat();
    const wordCells = new Set(
      this.board?.targets.flatMap((p) => p.path.map(([c, r]) => `${c},${r}`)) ?? [],
    );

    // Beat one: the distractor letters, staggered along the diagonal so the ice reads as a sweep
    // across the board and not as a single flat repaint.
    for (const t of tiles) {
      if (wordCells.has(`${t.col},${t.row}`)) continue;
      this.growIce(t, FROZEN_FILL, FROZEN_TEXT, 0xffffff, (t.col + t.row) * 16);
    }
    playCrackSound();

    // Beat two: the words, once the noise around them has gone quiet — and a full second later,
    // because the pause is what makes them read as an answer rather than as more of the freeze.
    this.time.delayedCall(1000, () => {
      if (this.over) return;
      for (const p of this.board?.targets ?? []) {
        // A word the child already took keeps its blue: in this mode blue means "you took this one"
        // and violet means "this was one of them". Repainting a found word would take the credit
        // back at the last moment.
        if (this.foundIds.has(p.word.id)) continue;
        p.path.forEach(([c, r], i) => {
          const tile = this.tiles[r]?.[c];
          if (tile) this.growIce(tile, REVEAL_FILL, REVEAL_TEXT, 0xe3d3ff, i * 55);
        });
      }
    });

    // The words then hold for a beat over a second before the board breaks. They are the reason
    // this sequence exists — four words is more than a five-year-old reads in half a second — and
    // the shatter is only the punctuation.
    this.time.delayedCall(2700, () => {
      if (this.over) return;
      playCrackSound();
      for (const t of tiles) {
        this.tweens.add({
          targets: t.ice ? [t.plate, t.label, t.ice] : [t.plate, t.label],
          y: `+=${18 + Math.random() * 26}`,
          alpha: 0,
          scale: 0.4,
          angle: (Math.random() - 0.5) * 40,
          duration: 700,
          delay: Math.random() * 160,
          ease: 'Quad.easeIn',
        });
      }
      this.loseFlake();
      if (this.flakes === 0) return;   // gameOver is already scheduled
      this.time.delayedCall(820, () => {
        if (this.over) return;
        this.banner(i18next.t('maze.timeUp'), '#bfefff', () => this.startBoard(this.round));
      });
    });
  }

  /**
   * Ice growing over one tile: a crystal that spreads from a corner across the face, after which
   * the tile itself is left frozen.
   *
   * Drawn once and *scaled* rather than redrawn per frame — 104 tiles redrawing a dozen line
   * segments each would be a stutter on a phone, and a transform is free. The crystal's origin is
   * the corner it starts from, so scaling it up looks like growth from that corner rather than
   * something appearing in the middle and inflating.
   *
   * A dendrite, not a snowflake glyph: three needles with side branches is what frost on a window
   * actually does, and at 66-105 px it reads at a glance where a six-fold flake turns to mush.
   */
  private growIce(tile: Tile, fill: number, text: string, crystal: number, delay: number): void {
    const s = this.cell - 6;
    const { x, y } = this.cellCentre(tile.col, tile.row);
    // Start from one of the four corners, chosen per tile so the board does not freeze in lockstep.
    const sx = Math.random() < 0.5 ? -1 : 1;
    const sy = Math.random() < 0.5 ? -1 : 1;
    const g = this.add.graphics().setDepth(15).setPosition(x + (sx * s) / 2, y + (sy * s) / 2);

    // The pane of ice: the tile's own square, in the crystal's frame (its top-left relative to the
    // corner this is growing from).
    g.fillStyle(crystal, 0.5);
    g.fillRoundedRect(sx < 0 ? 0 : -s, sy < 0 ? 0 : -s, s, s, 10);

    // Needles from the corner, with two branches each — the shape frost makes on glass.
    g.lineStyle(Math.max(1.5, s * 0.03), crystal, 0.95);
    const base = Math.atan2(-sy, -sx);
    for (const spread of [-0.5, 0, 0.5]) {
      const a = base + spread;
      const len = s * (0.78 + Math.random() * 0.25);
      const ex = Math.cos(a) * len;
      const ey = Math.sin(a) * len;
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(ex, ey);
      g.strokePath();
      for (const at of [0.42, 0.72]) {
        for (const side of [-0.7, 0.7]) {
          const bx = Math.cos(a) * len * at;
          const by = Math.sin(a) * len * at;
          g.beginPath();
          g.moveTo(bx, by);
          g.lineTo(bx + Math.cos(a + side) * len * 0.28, by + Math.sin(a + side) * len * 0.28);
          g.strokePath();
        }
      }
    }

    g.setScale(0).setAlpha(0);
    this.frost.push(g);
    tile.ice = g;
    this.tweens.add({
      targets: g,
      scale: 1,
      alpha: 1,
      duration: 420,
      delay,
      ease: 'Cubic.easeOut',
      // The tile turns under the ice as it closes over, so the two never disagree about what is
      // frozen: growth first, then the face.
      onComplete: () => {
        this.setTileFill(tile.plate, fill);
        tile.label.setColor(text);
      },
    });
  }

  private banner(text: string, color: string, then: () => void): void {
    const label = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, text, {
      fontFamily: FONT_STACK, fontSize: '52px', color, align: 'center', padding: { x: 30, y: 18 },
    }).setOrigin(0.5).setDepth(100);
    const bg = addRoundedLabelBg(this, label, { radius: 22 });
    this.time.delayedCall(1200, () => {
      label.destroy();
      bg.destroy();
      then();
    });
  }

  // ── HUD ──────────────────────────────────────────────────────────────────────

  private drawBackdrop(): void {
    const bg = getBackground();
    if (bg === 'space') { new Starfield(this); return; }
    const key = `bg_${backgroundAsset(bg)}`;
    if (this.textures.exists(key)) {
      const img = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, key);
      img.setScale(Math.max(GAME_WIDTH / img.width, GAME_HEIGHT / img.height));
      img.setDepth(-1);
    } else {
      new Starfield(this);
    }
  }

  private drawHud(): void {
    // The round mode's HUD, corner for corner, exactly as Pairs lays it out: back pill top-left
    // with the lives directly under it, the run's readouts stacked top-right, mute bottom-left.
    const back = this.drawBackButton();

    const flakesY = back.y + back.height + 10;
    this.flakeIcons = [];
    for (let i = 0; i < MAZE.startFlakes; i++) {
      this.flakeIcons.push(this.add.text(back.x + i * 40, flakesY, FLAKE, {
        fontFamily: FONT_STACK, fontSize: '30px',
      }).setDepth(40));
    }
    this.paintFlakes();

    this.roundText = this.add.text(GAME_WIDTH - 24, 20, i18next.t('maze.round', { n: this.round }), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffd166',
    }).setOrigin(1, 0).setDepth(40);

    this.foundText = this.add.text(GAME_WIDTH - 24, 58, '', {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#ffffff',
    }).setOrigin(1, 0).setDepth(40);

    // The ask goes where every lesson puts its question: a gold pill across the top, on the same
    // line and in the same size and colour as BoardScene's and Survival's rule banner. A child
    // crossing from the lesson into the reward should find the question where they already look.
    // Tapping it re-speaks it, free and as often as they like.
    this.askText = this.add.text(GAME_WIDTH / 2, BANNER_Y, '', {
      fontFamily: FONT_STACK, fontSize: '34px', color: '#ffd166', align: 'center',
      padding: { x: 18, y: 10 },
    }).setOrigin(0.5).setDepth(50).setInteractive({ useHandCursor: true });
    this.askText.on('pointerdown', () => {
      if (this.running) speakRulePrompt(this.askText.text, this.lang);
    });

    // The clock is a bar, not a number: this mode is played by children who cannot yet read a
    // countdown, and a bar says "hurry" without saying anything.
    const barX = GAME_WIDTH / 2 - CLOCK_W / 2;
    this.add
      .nineslice(barX, CLOCK_Y, pillTexture(this, 7, 0x1a2430, 0.75), undefined, CLOCK_W, 14, 7, 7, 7, 7)
      .setOrigin(0, 0).setDepth(40);   // the track: drawn once, never touched again
    this.clockFill = this.add
      .nineslice(barX, CLOCK_Y, pillTexture(this, 7, CLOCK_FILL, 0.95), undefined, 4, 14, 7, 7, 7, 7)
      .setOrigin(0, 0).setDepth(40);
  }

  /**
   * The ask, with its pill redrawn to fit.
   *
   * The pill is a Graphics sized at the moment it is added (ui/roundedLabelBg), so setting the text
   * afterwards would leave last board's plate — or, on the first board, a plate sized to the empty
   * string. Cheaper to redraw one rectangle per board than to teach the helper to resize.
   */
  private setAsk(text: string): void {
    this.askText.setText(text);
    this.askBg?.destroy();
    this.askBg = addRoundedLabelBg(this, this.askText);
    // Keep the tap target on the text itself — the pill is decoration and sits behind it.
    this.askText.setInteractive({ useHandCursor: true });
  }

  private paintClock(): void {
    const board = this.board;
    if (!board) return;
    const frac = Phaser.Math.Clamp(this.msLeft / board.timeMs, 0, 1);
    const px = Math.max(4, Math.round(CLOCK_W * frac));
    const low = frac < 0.25;
    // The bar is a stretched nine-slice, so a frame that shows the same bar as the last one costs
    // nothing at all — this early return only saves the resize itself. It was worth much more when
    // the bar was a Graphics redrawn from two rounded rects (four arcs apiece, ~100 tessellated
    // points each) on all ~3000 frames of a board, for a width that changes a few hundred times.
    if (px === this.clockPx && low === this.clockLow) return;
    if (low !== this.clockLow) this.clockFill.setTexture(pillTexture(this, 7, low ? CLOCK_LOW : CLOCK_FILL, 0.95));
    this.clockPx = px;
    this.clockLow = low;
    this.clockFill.setSize(px, 14);
  }

  /** A melted flake is the same glyph dimmed — the trick Pairs uses for a spent brain. */
  private paintFlakes(): void {
    this.flakeIcons.forEach((f, i) => f.setAlpha(i < this.flakes ? 1 : 0.3));
  }

  private drawBackButton(): Phaser.GameObjects.Text {
    const back = this.add.text(12, 16, `← ${i18next.t('nav.menu')}`, {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 20, y: 12 },
    }).setOrigin(0, 0).setDepth(50);
    back.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(
        -back.x, -back.y, back.width + back.x + 10, back.height + back.y + 10,
      ),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });
    addRoundedLabelBg(this, back);
    back.on('pointerover', () => back.setColor('#ffffff'));
    back.on('pointerout', () => back.setColor('#dfe6ee'));
    // Straight out, no confirm — the decision Pairs settled (a child who taps back has decided,
    // and a two-button question they must read their way out of is the heavier cost). Leaving does
    // forfeit the Ice Cube; it was spent on entry.
    back.on('pointerdown', () => this.scene.start('Menu'));
    return back;
  }

  // ── overlays ─────────────────────────────────────────────────────────────────

  private showHowTo(onStart: () => void): void {
    // The lesson's help panel (HelpScene), plate for plate — the same card Pairs opens on, for the
    // same reason: that is where a child has already met "here is the rule, tap when you have it".
    const layer = this.add.container(0, 0).setDepth(200);
    const shade = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85,
    );
    shade.setInteractive();
    const panel = this.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH * 0.85, GAME_HEIGHT * 0.78, 0x1a1f25,
    ).setStrokeStyle(4, 0xffd166);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 210, i18next.t('maze.howToTitle'), {
      fontFamily: FONT_STACK, fontSize: '64px', color: '#ffd166', fontStyle: 'bold',
    }).setOrigin(0.5);
    const body = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, i18next.t('maze.howToBody'), {
      fontFamily: FONT_STACK, fontSize: '28px', color: '#ffffff', align: 'center',
      wordWrap: { width: GAME_WIDTH * 0.72 }, lineSpacing: 8,
    }).setOrigin(0.5);
    const flakes = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 90, FLAKE.repeat(MAZE.startFlakes), {
      fontFamily: FONT_STACK, fontSize: '38px',
    }).setOrigin(0.5);
    const start = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 200, i18next.t('maze.howToStart'), {
      fontFamily: FONT_STACK, fontSize: '36px', color: '#ffffff',
      backgroundColor: '#7ec4ff', padding: { x: 40, y: 14 }, fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    layer.add([shade, panel, title, body, flakes, start]);

    // Tap anywhere to start, behind a TIMESTAMP guard (not a delayed registration) so the tap that
    // spent the Ice Cube can't skip the card it just opened, and a stalled scene clock — a
    // backgrounded tab — can never strand the child on the card. Same as Pairs §8.1.
    const openedAt = performance.now();
    const dismiss = (): void => {
      if (performance.now() - openedAt < 350) return;
      this.input.off('pointerdown', dismiss);
      layer.destroy(true);
      onStart();
    };
    this.input.on('pointerdown', dismiss);
  }

  // ── end of run ───────────────────────────────────────────────────────────────

  private gameOver(): void {
    if (this.over) return;
    this.over = true;
    this.running = false;
    this.clearSelection();

    // Show what was hidden: the answer is the consolation for losing, and a child who never sees
    // the board resolved learns nothing from the run that ended.
    for (const p of this.board?.targets ?? []) {
      if (this.foundIds.has(p.word.id)) continue;
      for (const [c, r] of p.path) {
        const tile = this.tiles[r]?.[c];
        if (!tile) continue;
        // Violet, the same colour the time-up freeze reveals with: in this mode blue means "you
        // took this one" and violet means "this was one of them". Painting a missed word blue
        // would quietly credit the child with a word they never found.
        this.setTileFill(tile.plate, REVEAL_FILL);
        tile.label.setColor(REVEAL_TEXT);
      }
    }

    trackMazeEnd(this.wordsFound);   // web demo only

    const isRecord = this.wordsFound > this.best;
    if (isRecord) { setBestMaze(this.wordsFound); this.best = this.wordsFound; playRecordSound(); }
    else playRoundCompleteSound();

    const headline = isRecord ? i18next.t('maze.newBest') : i18next.t('maze.gameOver');
    const message = `${headline}\n${i18next.t('maze.found', { n: this.wordsFound })}`;
    const overlay = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, message, {
      fontFamily: FONT_STACK, fontSize: '60px', color: isRecord ? '#4ecdc4' : '#ffd166',
      padding: { x: 34, y: 20 }, align: 'center',
    }).setOrigin(0.5).setAlpha(0).setScale(0.8).setDepth(100);
    const overlayBg = addRoundedLabelBg(this, overlay, { radius: 24 });
    overlayBg.setAlpha(0).setScale(0.8);

    const cont = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, i18next.t('maze.continue'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#dfe6ee', padding: { x: 16, y: 9 },
    }).setOrigin(0.5).setDepth(100);
    addRoundedLabelBg(this, cont);

    this.tweens.add({ targets: [overlay, overlayBg], alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut' });

    const shownAt = performance.now();
    const leave = (): void => {
      if (performance.now() - shownAt < 900) return;
      this.input.off('pointerdown', leave);
      this.scene.start('Menu');
    };
    this.input.on('pointerdown', leave);

    maybeShowBuyPrompt(this, { delayMs: 1500 });
    maybeAskForRating(this, { kind: 'maze', words: this.wordsFound });
  }
}

/** Resolve a chosen background to its image asset slug (fable has day/night variants). */
function backgroundAsset(bg: Background): string {
  return bg === 'fable' ? fableVariant() : bg;
}
